import { formatSwedishSek } from "./money";
import { captureAuthenticationGeneration, signalAuthenticationExpired } from "./authCache";

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) { super(message); }
}

async function authenticatedFetch(path: string, init?: RequestInit) {
  const authenticationGeneration = captureAuthenticationGeneration();
  const response = await fetch(`/api${path}`, { ...init, credentials: "include" });
  return { response, authenticationGeneration };
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body && !(init.body instanceof FormData)) headers.set("Content-Type", "application/json");
  const { response, authenticationGeneration } = await authenticatedFetch(path, { ...init, headers });
  const type = response.headers.get("content-type") ?? "";
  const body = type.includes("json") ? await response.json() : await response.text();
  if (!response.ok) {
    const error = body?.error;
    const gatewayError = response.status === 413
      ? { code: "file_too_large", message: "Filen är för stor för att laddas upp." }
      : undefined;
    const code = error?.code ?? gatewayError?.code ?? "request_error";
    if (response.status === 401 && (code === "unauthenticated" || code === "session_expired"))
      signalAuthenticationExpired(authenticationGeneration);
    throw new ApiError(
      response.status,
      code,
      error?.message ?? gatewayError?.message ?? "Begäran misslyckades",
      error?.details ?? error?.fields,
    );
  }
  return body as T;
}

export function companyApiPath(companyId: string, path: string): string {
  if (!companyId) throw new Error("Välj en förening och försök igen.");
  if (!path.startsWith("/")) throw new Error("API-sökvägen måste börja med /.");
  return `/companies/${encodeURIComponent(companyId)}${path}`;
}

export function companyApi<T>(companyId: string, path: string, init?: RequestInit): Promise<T> {
  return api<T>(companyApiPath(companyId, path), init);
}

export async function companyContent(companyId: string, path: string, init?: RequestInit) {
  const { response, authenticationGeneration } = await authenticatedFetch(companyApiPath(companyId, path), init);
  if (!response.ok) {
    const type = response.headers.get("content-type") ?? "";
    const body = type.includes("json") ? await response.json() : null;
    const error = body?.error;
    const code = error?.code ?? "content_request_failed";
    if (response.status === 401) signalAuthenticationExpired(authenticationGeneration);
    throw new ApiError(
      response.status,
      code,
      error?.message ?? "Underlaget kunde inte hämtas.",
      error?.details ?? error?.fields,
    );
  }
  return response;
}

function temporaryObjectUrl(blob: Blob) {
  const url = URL.createObjectURL(blob);
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return url;
}

/** Download authenticated tenant content through the generation-aware boundary. */
export async function downloadCompanyContent(
  companyId: string,
  path: string,
  fileName: string,
) {
  const response = await companyContent(companyId, path, { cache: "no-store" });
  const anchor = document.createElement("a");
  anchor.href = temporaryObjectUrl(await response.blob());
  anchor.download = fileName;
  anchor.hidden = true;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

/** Open authenticated tenant content without bypassing current/stale 401 handling. */
export async function openCompanyContent(companyId: string, path: string) {
  const opened = window.open("about:blank", "_blank");
  if (opened) opened.opener = null;
  try {
    const response = await companyContent(companyId, path, { cache: "no-store" });
    const url = temporaryObjectUrl(await response.blob());
    if (!opened) throw new Error("Webbläsaren blockerade det nya fönstret.");
    opened.location.replace(url);
  } catch (cause) {
    opened?.close();
    throw cause;
  }
}

export function companyQueryScope(companyId: string): string {
  if (!companyId) throw new Error("Välj en förening och försök igen.");
  return `company:${companyId}`;
}

/**
 * Tenant API entry point for components rendered below /app/:companyId. The
 * caller must capture the route company when it creates a query or mutation;
 * deferred work must never re-read mutable browser location state.
 */
export function routeCompanyApi<T>(companyId: string, path: string, init?: RequestInit): Promise<T> {
  return companyApi<T>(companyId, path, init);
}

type ClientRandomSource = {
  randomUUID?: () => string;
  randomBytes: () => Uint8Array;
};

export function clientId(randomSource: ClientRandomSource = {
  randomUUID: globalThis.crypto.randomUUID?.bind(globalThis.crypto),
  randomBytes: () => globalThis.crypto.getRandomValues(new Uint8Array(16)),
}): string {
  if (typeof randomSource.randomUUID === "function") return randomSource.randomUUID();
  const bytes = randomSource.randomBytes();
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function idempotencyKey(prefix: string): string { return `${prefix}-${clientId()}`; }

export function formatSek(value: string | number | null | undefined): string {
  return formatSwedishSek(String(value ?? "0"));
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  return /^(\d{4}-\d{2}-\d{2})/.exec(value)?.[1] ?? "—";
}

export function swedishDateFromNow(offsetDays = 0): string {
  const now = new Date();
  const stockholm = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Stockholm", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(stockholm.find((entry) => entry.type === type)?.value ?? 0);
  const shifted = new Date(Date.UTC(part("year"), part("month") - 1, part("day") + offsetDays, 12));
  const result = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Stockholm", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(shifted);
  const text = (type: Intl.DateTimeFormatPartTypes) =>
    result.find((entry) => entry.type === type)?.value ?? "";
  return `${text("year")}-${text("month")}-${text("day")}`;
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const parts = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Stockholm",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((entry) => entry.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")} ${part("hour")}:${part("minute")}`;
}
